import * as React from 'react';

import { ComboBox } from '@rematter/ui-web';
import { humanReadableAddress, cn } from '@rematter/ui-web/lib';
import {
  LucideBriefcaseBusiness,
  LucideMapPin,
  LucideUser,
} from 'lucide-react';

import { useAccountAddressesQuery } from '../../hooks/queries/useAccountAddressesQuery';
import { useFacilitiesQuery } from '../../hooks/queries/useFacilitiesQuery';

import useDebouncedValue from '~/shared/hooks/useDebounce';

type LocationOption = {
  id: string;
  name: string;
  subtitle: string;
  type: 'facility' | 'account';
  accountType?: string;
};

type LocationTypeValue = 'Facility' | 'AccountAddress';

interface LocationPickerFieldProps {
  label: string;
  value: string | null;
  onChange: (
    locationId: string | null,
    locationName: string | null,
    locationType: LocationTypeValue | null,
  ) => void;
  isRequired?: boolean;
  isDisabled?: boolean;
}

export const LocationPickerField = ({
  label,
  value,
  onChange,
  isRequired,
  isDisabled,
}: LocationPickerFieldProps) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  const { data: facilitiesData } = useFacilitiesQuery({
    searchTerm: debouncedSearchTerm,
    limit: 10,
  });

  const { data: addressesData } = useAccountAddressesQuery({
    searchTerm: debouncedSearchTerm,
    limit: 10,
  });

  const allOptions = React.useMemo(() => {
    const facilities: LocationOption[] = (facilitiesData?.facilities ?? []).map(
      (f) => ({
        id: f.id,
        name: f.facilityName,
        subtitle: f.address?.streetAddress
          ? humanReadableAddress(f.address)
          : '',
        type: 'facility' as const,
      }),
    );

    const accounts: LocationOption[] = (addressesData?.addresses ?? []).map(
      (a) => ({
        id: a.id,
        name: a.account.name,
        subtitle: a.streetAddress ? humanReadableAddress(a) : '',
        type: 'account' as const,
        accountType: a.account?.type,
      }),
    );

    return [...facilities, ...accounts];
  }, [facilitiesData, addressesData]);

  const comboBoxOptions = React.useMemo(
    () =>
      allOptions.map((opt) => ({
        id: opt.id,
        label: opt.name,
        option: opt,
      })),
    [allOptions],
  );

  const handleSelectionChange = (values: string[]) => {
    const selected = allOptions.find((opt) => opt.id === values[0]);
    const locType: LocationTypeValue | null = selected
      ? selected.type === 'facility'
        ? 'Facility'
        : 'AccountAddress'
      : null;
    onChange(selected?.id ?? null, selected?.name ?? null, locType);
  };

  return (
    <ComboBox
      label={label}
      value={value ? [value] : []}
      isRequired={isRequired}
      isDisabled={isDisabled}
      onChange={handleSelectionChange}
      query={searchTerm}
      onChangeQuery={setSearchTerm}
      options={comboBoxOptions}
      renderOption={(option, state) => {
        const loc = option.option as LocationOption;
        const Icon =
          loc.type === 'facility'
            ? LucideMapPin
            : loc.accountType === 'Individual'
              ? LucideUser
              : LucideBriefcaseBusiness;

        return (
          <div
            className={cn(
              'flex flex-row justify-start gap-2 p-2',
              state.focused && 'bg-primary text-white',
            )}
          >
            <Icon size={14} />
            <div>
              <div
                className={cn(
                  'text-sm font-medium',
                  state.focused && 'text-white',
                )}
              >
                {loc.name}
              </div>
              {loc.subtitle && (
                <div
                  className={cn(
                    'text-xs',
                    state.focused ? 'text-white/75' : 'text-neutral/50',
                  )}
                >
                  {loc.subtitle}
                </div>
              )}
            </div>
          </div>
        );
      }}
      getSelection={(ids) => {
        const selected = allOptions.find((opt) => opt.id === ids[0]);
        return selected ? [{ label: selected.name }] : [];
      }}
      className="@LocationPickerField"
    />
  );
};
